// Signed-in identity, pinned to the foot of the sidebar.
//
// Rebuilt to `wireframes/react/src/components/Shell.tsx:143-215` on the owner's
// instruction of 2026-09-09 ("implement it with the UI/UX and behaviour of this
// component from the wireframes"). Three things changed, and the third is a
// reversal worth naming rather than burying:
//
//  1. The identity block IS the trigger. The drawing makes the avatar, name and
//     role one button that opens the menu; there is no separate `⋯` control.
//     R2 forbids a native `<button>`, so this is a `MudButton` whose `--_height`
//     is released to `auto` — AGE fixes `.control`'s block-size for a one-line
//     label, and this label is three lines.
//  2. The role switch is inline. The drawing lists every available role in the
//     menu with its organisation beneath, checks the active one, and switches in
//     place. It does not send the reader to a separate screen, so the item that
//     navigated to `/role-select` is gone. The route itself stays: UC12 still
//     meets a multi-role user at sign-in, and `RoleSelectPage` still serves it.
//  3. The interface language is NOT here at all. The drawing puts a globe button
//     beside the identity with its own menu, and this file carried one until the
//     owner's ruling of 2026-09-09: the gov.md strip now rides above every screen,
//     signed in or not, and it already shows Română · Русский · English in the
//     open. Two language controls on one screen is one too many, and the strip's
//     is the better of the two — three visible labels, each written in its own
//     language, against an icon that has to be opened to be read. See
//     `docs/ui-shape-govmd-bar.md §The languages`.
//
// REVERSAL — owner ruling of 2026-09-09 supersedes owner ruling 2026-09-06
// (finding D-14). D-14 put a required Registrar select on the identity's third
// line, because an administrator holding two reporting entities picked neither
// and everything was filed against the first. The drawing's third line is inert
// text, and the owner chose the drawing knowing this: a select cannot live
// inside a button (invalid HTML, and the menu trigger swallows the click), so
// making the block the trigger and keeping D-14 were mutually exclusive.
// `RegistrarSelector` is therefore not rendered here. The regression bit, and
// register entry UC11-05 (CF-27, 2026-10-12) settled it the way this paragraph
// foresaw: the working context has its own control in `Sidebar`, outside the
// trigger (docs/shape-ops-tenant-selector.md §6).
//
// The menu itself is MUD's `mud-menu` (059 T032, per
// docs/shape-ops-mud-1-2-migration.md): a `selection` menu for the roles, with
// the checkmark on the active one, and a `contextual` menu for the way out. One
// menu has one type, hence two, in the one overlay the rail positions. mud-menu
// owns Enter, Space and Escape; this file owns the arrows, Home and End across
// the two menus, where the focus lands on open and where it returns on close.
//
// caiet UC10-07/08 is untouched by all of this: the name and the active role are
// visible at all times either way — being inside a button hides nothing.
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { ComponentRef, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudAvatar, MudButton, MudMenu, MudMenuItem, MudSeparator } from '@egov-moldova/mud-react';
import { isObserverRole, type SessionUser } from '@simsm/shared-types';
import { useObserverScope } from '../../features/observer/use-observer-scope';
import { useSession } from '../../hooks/use-session';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';
import { roleLabelKey, useScopedRoleLabel } from '../../hooks/use-observer-roles';
import { focusMenuItemWhenReady } from '@simsm/ui-shared';
import { apiClient } from '../../app/api-client';
import { homePathFor } from '../../app/role-home';
import { clearSessionEstablished } from '../../app/session-presence';
import { applyRoleSwitch, clearActiveRegistrar } from '../../app/active-registrar';

const LOGOUT_ACTION = '/api/v1/auth/logout';

type MenuRow = HTMLElement & { disabled?: boolean; heading?: boolean };

const ROVING_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

// mud-menu's own arithmetic (wrapping, and Home/End), over a longer list.
function nextRow(key: string, current: number, count: number): number {
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (current < 0) return key === 'ArrowDown' ? 0 : count - 1;
  return (current + (key === 'ArrowDown' ? 1 : -1) + count) % count;
}

// mud-menu roves only over its own rows, and the roles and the way out are two
// menus (one menu has one type). Without this the arrows stopped at the last
// role and "Ieșire" was reachable by Tab alone. In the capture phase, before
// either menu sees the key, the two are walked as one list.
function roveAcrossMenus(event: ReactKeyboardEvent<HTMLElement>): void {
  if (!ROVING_KEYS.has(event.key)) return;
  const rows = Array.from(event.currentTarget.querySelectorAll<MenuRow>('mud-menu-item')).filter(
    (row) => row.disabled !== true && row.heading !== true,
  );
  if (rows.length === 0) return;
  const index = nextRow(event.key, rows.indexOf(document.activeElement as MenuRow), rows.length);
  const target = rows[index];
  if (!target) return;
  event.preventDefault();
  event.stopPropagation();
  for (const row of rows) {
    if (row.parentElement === target.parentElement) row.tabIndex = row === target ? 0 : -1;
  }
  target.focus();
}

export function SidebarAccount(): JSX.Element | null {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionQuery = useSession();
  const session = sessionQuery.data;
  const roleLabelOf = useScopedRoleLabel();
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const roleMenuRef = useRef<ComponentRef<typeof MudMenu> | null>(null);
  const { scope } = useObserverScope(session?.activeRole);
  const activeRegistrar = useActiveRegistrar();

  function refocusTrigger(): void {
    rootRef.current?.querySelector<HTMLElement>('.simsm-sidebar__account-trigger')?.focus();
  }

  function closeAndRefocus(): void {
    setMenuOpen(false);
    refocusTrigger();
  }

  // The drawing switches role in place (`Shell.tsx:160-172`), so the mutation
  // that `RoleSelectPage` owns is run from here too: same endpoint, same cache
  // write, same landing. Nothing new is invented for the menu.
  const switchRole = useMutation({
    mutationFn: async (role: string) => {
      const { data } = await apiClient.patch<SessionUser>('/auth/active-role', {
        activeRole: role,
      });
      return data;
    },
    onSuccess: (next) => {
      // The menu is gone from the page before the session names the new role
      // (059 V-137). Closed after it, the menu was still mounted when the role
      // changed, the landing below ran again and moved the focus onto a row, and
      // the row then unmounted under it: the focus fell to <body>.
      flushSync(() => setMenuOpen(false));
      refocusTrigger();
      // The Registrar being worked on, and every answer cached under the role that
      // was left, belong to that role (BUG-1-001).
      applyRoleSwitch(queryClient, next);
      navigate(homePathFor(next.activeRole), { replace: true });
    },
    // mud-menu moves its own checkmark the moment a row is chosen. A refused
    // switch leaves the session on the old role, so the mark goes back to it.
    onError: () => {
      const active = sessionQuery.data?.activeRole;
      if (roleMenuRef.current && active) roleMenuRef.current.value = active;
    },
  });

  // Focus lands inside the menu on open: on the active role when there is a
  // choice of roles, else on the first row. The menus cannot take focus before
  // they have rendered, which is after this effect first runs, so the helper
  // waits for them (059 V-094) instead of calling `focus()` into a menu that is
  // not drawn yet. The landing is decided once, when the menu opens: the role is
  // read through a ref so that a role changing under an open menu does not run
  // the landing again and pull the focus back into a menu that is closing.
  const activeRoleRef = useRef(session?.activeRole);
  activeRoleRef.current = session?.activeRole;
  useEffect(() => {
    if (!menuOpen) return undefined;
    return focusMenuItemWhenReady({
      menu: overlayRef.current,
      pick: (items) => items.find((item) => item.value === activeRoleRef.current),
    });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onPointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  if (!session) return null;

  const fullName = `${session.firstName} ${session.lastName}`;
  // caiet UC61-02/03: an observer role minted in MPass after this build has no
  // translated name, so the label falls back to the name the System
  // Administrator gave the role and then to the raw code. No list of observer
  // codes lives here.
  //
  // caiet UC10-08 / NC-ACT-04: the Operator and the Administrator of an IMSP
  // Registrar read as their IMSP variants, decided by the types of the Registrars
  // in the role's own scope (docs/shape-ops-role-label-imsp.md §2).
  const activeRole = session.activeRole;
  const activeKinds = session.registrarKinds;
  const labelKey = roleLabelKey(activeRole, activeKinds);
  const scopeName = scope?.displayName ?? '';
  const roleLabel = labelKey ? t(labelKey) : scopeName || session.activeRole;
  // The bare IDNO is never printed here any more (owner instruction
  // 2026-09-09). `useActiveRegistrar` falls back to the identifier when the
  // catalog does not know the Registrar — owner decision D5, so that "I do not
  // know this Registrar" and "this Registrar is withdrawn" read differently
  // — and that fallback surfaced a 13-digit number where a name belongs. The
  // hook keeps it for its other five callers; only this line drops it, and shows
  // nothing rather than a number.
  const registrarName = activeRegistrar.name === activeRegistrar.idno ? '' : activeRegistrar.name;
  const institution =
    session.activeRole === 'SYSTEM_ADMIN' ? t('org.amdm') : scopeName || registrarName;
  // An observer role is usually named after its institution; repeating that name
  // on a second line says nothing.
  const organisation = institution === roleLabel ? '' : institution;
  const roles = session.availableRoles;
  const scopes = session.scopes;
  // caiet UC11-03: a user with one role is shown no role selection, at sign-in
  // or later. The identity block names the role at all times (UC10-08), so with
  // one role the menu holds the sign-out alone.
  const switchableRoles = roles.length > 1 ? roles : [];

  // The Registrar types of a role's own scope (caiet UC10-04, CF-26): the flat
  // field is the active role's, every other role reads its entry in `scopes`.
  function kindsOf(role: string): ReadonlyArray<string> | undefined {
    return role === activeRole ? activeKinds : scopes?.[role]?.registrarKinds;
  }

  // A role's label the same way the active one is resolved above: the caiet's
  // fixed codes carry a translation key (the IMSP variants by the role's own
  // Registrar types), an observer role is named by the directory, and an unknown
  // code prints as itself.
  function labelOf(role: string): string {
    return roleLabelOf(role, kindsOf(role));
  }

  // The drawing writes each role's organisation beneath its name
  // (`Shell.tsx:167`, `<span className="m-sub">{v.org}</span>`). What may be said
  // there is bounded by what the session carries, and the resolution is
  // `RoleSelectPage.organisationFor`'s — owner decision D5 keeps
  // `registrarNames` index-aligned with `registrarScope`, more than one
  // Registrar names none of them, and a line that would only repeat the role's
  // own name is not drawn at all. Each role reads its own scope (caiet UC10-04).
  function organisationFor(role: string, label: string): string {
    const registrarScope = scopes?.[role]?.registrarScope ?? [];
    const registrarNames = scopes?.[role]?.registrarNames ?? [];
    const named = ((): string => {
      if (role === 'SYSTEM_ADMIN') return t('org.amdm');
      if (isObserverRole(role)) {
        const observerName = roleLabelOf(role, undefined);
        return observerName === role ? '' : observerName;
      }
      if (registrarScope.length === 1) {
        // Same rule as the identity line above: a name or nothing, never the
        // IDNO that D5's fallback would otherwise put here.
        return registrarNames[0] || '';
      }
      if (registrarScope.length > 1) {
        return t('role_select.org.registrar_count', { count: registrarScope.length });
      }
      return '';
    })();
    return named === label ? '' : named;
  }

  // caiet UC12-01: sign-out is a top-level form POST, never an XHR. The bff
  // answers with a redirect to MPass Single Logout, which only a navigating
  // browser follows, and it decides whether MPass is involved at all; the
  // bundle does not (docs/shape-ops-mpass-single-logout.md §2).
  function handleLogout(): void {
    setMenuOpen(false);
    // A deliberate sign-out is not an expiry: forget that this browser held a
    // session so the next 401 sends the reader to the landing page rather than
    // to a screen announcing an expiry they caused themselves.
    clearSessionEstablished();
    // The active Registrar ends with the server session the sign-out destroys;
    // the browser forgets which one its pages were for.
    clearActiveRegistrar();
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = LOGOUT_ACTION;
    document.body.append(form);
    form.submit();
  }

  const identityLines = organisation
    ? `${fullName}, ${roleLabel}, ${organisation}`
    : `${fullName}, ${roleLabel}`;

  // Escape belongs to the account menu while it is open. Under 1024px the rail
  // is an off-canvas panel that closes on Escape at the document; the key stops
  // here, so it closes the menu and the focus goes back to its trigger, inside a
  // panel that stays open.
  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Escape' || !menuOpen) return;
    event.stopPropagation();
    closeAndRefocus();
  }

  return (
    <div ref={rootRef} className="simsm-sidebar__account" onKeyDown={handleKeyDown}>
      <MudButton
        className="simsm-sidebar__account-trigger"
        shape="rectangular"
        variant="strict"
        appearance="text"
        size="md"
        fullWidth
        title={identityLines}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <MudAvatar slot="icon-start" type="initials" size="sm" name={fullName} alt="" />
        <span className="simsm-sidebar__identity">
          <span className="simsm-sidebar__identity-name">{fullName}</span>
          <span className="simsm-sidebar__identity-role">{roleLabel}</span>
          {organisation ? (
            <span className="simsm-sidebar__identity-org">{organisation}</span>
          ) : null}
        </span>
        {/* Empty on purpose: it is here to absorb the free space, not to show
            anything. AGE hard-codes `justify-content: center` on `.control`
            (`mud-button.css:230`) with no custom property to override it, and
            the identity sits inside the shadow root's `.label`, out of reach.
            Content in `icon-end` IS a direct flex child of `.control` — a
            `<slot>` is `display: contents` — so an auto margin there takes the
            slack and leaves the avatar and the three lines against the start
            edge. */}
        <span slot="icon-end" className="simsm-sidebar__account-spacer" aria-hidden="true" />
      </MudButton>

      {menuOpen ? (
        // The anchored overlay: placement and the panel's chrome stay here. The
        // semantics are the two mud-menus inside it, which name themselves.
        <div className="simsm-sidebar__menu" ref={overlayRef} onKeyDownCapture={roveAcrossMenus}>
          {/* No identity header, deliberately, and this is the one place the
              panel departs from `1210:4936`.

              MUD puts the identity at the top of the panel because ITS trigger
              is a bare avatar in the header bar — the panel is the only place
              the name and the capacity appear. MUD's own Sidebar (`1236:6302`)
              carries navigation and nothing else: it has no account block, and
              the two `role-switch` instances that sit near it on the Header page
              (`1236:6844`, `1236:7252`) are inside 375x812 iPhone frames, the
              mobile takeover — MUD never composes this panel with a rail.

              SIMSM has no header bar on the authenticated screens (spec 032), so
              the identity lives at the foot of the rail, where caiet UC10-07/08
              requires the name and the active role to be visible AT ALL TIMES.
              The trigger below therefore already says everything MUD's header
              block would, six pixels away. Reproducing it would either repeat
              the identity twice or force the trigger down to an avatar, which
              UC10-07/08 forbids. */}
          {/* MUD's body container (`1210:4887`) holds the label, the rows and
              the sign-out inside a 16px inset. The inset has to live HERE and
              not as a margin on the children: those are `fullWidth`, so AGE
              sizes them to 100% of this box and a margin would be added outside
              that — which is exactly how they came to hang 32px past the
              panel's edge. */}
          <div className="simsm-sidebar__menu-body">
            {switchableRoles.length > 0 ? (
              <>
                {/* The heading is read as the listbox's own name below, so it
                    is hidden from a reader rather than announced twice. */}
                <span className="simsm-sidebar__menu-heading" aria-hidden="true">
                  {t('account.switch_role')}
                </span>
                {/* A selection menu: the roles are one choice, the active one
                    carries mud-menu's trailing checkmark, and a row reports its
                    role in `value`. The organisation sits under the name in the
                    row's slot (`Shell.tsx:167`). */}
                <MudMenu
                  className="simsm-sidebar__menu-roles"
                  type="selection"
                  value={session.activeRole}
                  aria-label={t('account.switch_role')}
                  ref={roleMenuRef}
                  onMudSelect={(event) => {
                    const role = event.detail.value;
                    if (role === session.activeRole) {
                      closeAndRefocus();
                      return;
                    }
                    switchRole.mutate(role);
                  }}
                  onMudClose={closeAndRefocus}
                >
                  {switchableRoles.map((role) => {
                    const label = labelOf(role);
                    const org = organisationFor(role, label);
                    return (
                      <MudMenuItem key={role} value={role} disabled={switchRole.isPending}>
                        <span className="simsm-sidebar__menu-role-text">
                          <span className="simsm-sidebar__menu-role-name">{label}</span>
                          {org ? <span className="simsm-sidebar__menu-role-org">{org}</span> : null}
                        </span>
                      </MudMenuItem>
                    );
                  })}
                </MudMenu>
                <MudSeparator inset />
              </>
            ) : null}

            {/* The way out is an action, not a choice, so it is a contextual
                menu of its own: one menu has one type. The wireframe's globe
                menu is not here (see the header comment): the gov.md strip
                carries the language. */}
            <MudMenu
              className="simsm-sidebar__menu-actions"
              type="contextual"
              aria-label={t('account.menu.aria_label', { name: fullName })}
              onMudSelect={handleLogout}
              onMudClose={closeAndRefocus}
            >
              <MudMenuItem value="logout" leading="icon" icon="logout">
                {t('user_menu.logout')}
              </MudMenuItem>
            </MudMenu>
          </div>
        </div>
      ) : null}
    </div>
  );
}
